import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { CrmPaginationComponent } from '../crm-pagination/crm-pagination.component';
import { AddContactModalComponent } from './add-contact-modal/add-contact-modal.component';
import { ViewContactModalComponent } from './view-contact-modal/view-contact-modal.component';
import { AddDealModalComponent } from '../deals/add-deal-modal/add-deal-modal.component';
import * as CrmActions from '../../../state/crm/crm.actions';
import * as CrmSelectors from '../../../state/crm/crm.selectors';
import {MatTooltip} from "@angular/material/tooltip";
import { ReportsSortComponent, SortOption, SortChoice, DEFAULT_SORT } from '../../reports/reports-sort/reports-sort.component';
import { ReportsDateRangeComponent, DateRange, DEFAULT_DATE_RANGE } from '../../reports/reports-date-range/reports-date-range.component';

const CONTACT_SORT_CHOICES: SortChoice[] = [
  { label: 'Date created (newest first)', sort_by: 'created_at',  sort_dir: 'desc' },
  { label: 'Date created (oldest first)', sort_by: 'created_at',  sort_dir: 'asc'  },
  { label: 'Person Name A → Z',           sort_by: 'person_name', sort_dir: 'asc'  },
  { label: 'Person Name Z → A',           sort_by: 'person_name', sort_dir: 'desc' },
];

@Component({
  selector: 'app-contacts',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, CrmPaginationComponent, AddContactModalComponent, ViewContactModalComponent, MatTooltip, ReportsSortComponent, ReportsDateRangeComponent, AddDealModalComponent],
  templateUrl: './contacts.component.html',
  styleUrl: './contacts.component.scss'
})
export class ContactsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() searchQuery: string = '';
  @Output() searchQueryChange = new EventEmitter<string>();

  showAddContactModal: boolean = false;
  selectedContact: any = null;
  modalMode: 'create' | 'edit' = 'create';
  showViewContactModal: boolean = false;
  selectedContactId: string | null = null;

  // "Add deal" flow from a contact row
  showAddDealModal: boolean = false;
  preselectedContactIdForDeal: number | null = null;

  // Observables from store
  contacts$: Observable<any[]>;
  loading$: Observable<boolean>;
  error$: Observable<any>;
  pagination$: Observable<any>;
  companies$: Observable<any[]>;
  companiesLookup$: Observable<any[]>;
  contactsLookup$: Observable<any[]>;

  // Local state
  paginatedUsers: any[] = [];
  tableHeight: number = 0;
  pagination: any = {
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 10
  };

  currentSort: SortOption = { ...DEFAULT_SORT };
  currentDateRange: DateRange = DEFAULT_DATE_RANGE;
  readonly sortChoices: SortChoice[] = CONTACT_SORT_CHOICES;

  private destroy$ = new Subject<void>();
  private pendingCompanyId: number | null = null;

  constructor(private store: Store) {
    // Set up observables with updated selector names
    this.contacts$ = this.store.select(CrmSelectors.selectContacts);
    this.loading$ = this.store.select(CrmSelectors.selectContactsLoading);
    this.error$ = this.store.select(CrmSelectors.selectContactsError);
    this.pagination$ = this.store.select(CrmSelectors.selectContactsPagination);
    this.companies$ = this.store.select(CrmSelectors.selectCompanies);
    this.companiesLookup$ = this.store.select(CrmSelectors.selectCompaniesLookup);
    this.contactsLookup$ = this.store.select(CrmSelectors.selectContactsLookup);
  }

  ngOnInit(): void {
    this.updateTableHeight();

    // Subscribe to errors
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        console.error('CRM Error:', error);
      }
    });

    // Subscribe to pagination changes
    this.pagination$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pagination => {
        // console.log('Pagination from store:', pagination);
        this.pagination = pagination || {
          total: 0,
          per_page: 10,
          current_page: 1,
          last_page: 1,
          from: 0,
          to: 0
        };
      });

    // Subscribe to contacts changes
    this.contacts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(contacts => {
        // console.log('Contacts from store:', contacts);
        // Ensure contacts is always an array
        this.paginatedUsers = Array.isArray(contacts) ? contacts : [];
        // console.log('PaginatedUsers:', this.paginatedUsers);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Search is now triggered manually via button click or Enter key
    // Removed automatic search on searchQuery change to prevent search while typing
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.updateTableHeight();
  }

  updateTableHeight(): void {
    const headerHeight = 80; // CRM header
    const tabsHeight = 36; // Tabs
    const toolbarHeight = 42; // Period + Sort row (button height + mb-3 spacing)
    const paginationHeight = 36; // Pagination component height
    const padding = 112; // Various paddings

    this.tableHeight = window.innerHeight - headerHeight - tabsHeight - toolbarHeight - paginationHeight - padding;
  }

  onPageChanged(page: number): void {
    this.store.dispatch(CrmActions.loadContacts(this.buildLoadPayload({ page })));
  }

  onPerPageChanged(perPage: number): void {
    this.store.dispatch(CrmActions.loadContacts(this.buildLoadPayload({ page: 1, perPage })));
  }

  searchReport(): void {
    this.store.dispatch(CrmActions.loadContacts(this.buildLoadPayload({ page: 1 })));
  }

  onSortChanged(sort: SortOption): void {
    this.currentSort = sort;
    this.store.dispatch(CrmActions.loadContacts(this.buildLoadPayload({ page: 1 })));
  }

  onDateRangeChanged(range: DateRange): void {
    this.currentDateRange = range;
    this.store.dispatch(CrmActions.loadContacts(this.buildLoadPayload({ page: 1 })));
  }

  private buildLoadPayload(overrides: { page?: number; perPage?: number } = {}) {
    return {
      page: overrides.page ?? this.pagination.current_page ?? 1,
      perPage: overrides.perPage ?? this.pagination.per_page ?? 10,
      search: this.searchQuery,
      sortBy: this.currentSort.sort_by,
      sortDir: this.currentSort.sort_dir,
      dateRange: this.currentDateRange,
    };
  }

  viewUser(user: any): void {
    // console.log('View user:', user);
    this.selectedContactId = user.id.toString();
    this.showViewContactModal = true;
    this.store.dispatch(CrmActions.loadContactDetail({ id: user.id.toString() }));
  }

  editUser(user: any): void {
    // console.log('Edit user:', user);
    this.selectedContact = user;
    this.modalMode = 'edit';
    this.showAddContactModal = true;
  }

  deleteUser(user: any): void {
    if (confirm(`Are you sure you want to delete ${user.name} ${user.surname}?`)) {
      this.store.dispatch(CrmActions.deleteContact({ id: user.id }));
    }
  }

  addUser(): void {
    this.selectedContact = null;
    this.modalMode = 'create';
    this.showAddContactModal = true;
  }

  onCloseContactModal(): void {
    this.showAddContactModal = false;
  }

  onCloseViewContactModal(): void {
    this.showViewContactModal = false;
    this.selectedContactId = null;
  }

  onSaveContact(contactData: any): void {
    // Store the company ID for later use if it exists
    this.pendingCompanyId = contactData.company_id || null;

    // Remove company_id from contactData before sending to API
    const { company_id, ...contactDataWithoutCompany } = contactData;

    // The contact data is already in the correct format from the modal
    if (contactData.id) {
      // Update existing contact
      this.store.dispatch(CrmActions.updateContact({
        id: contactData.id,
        contact: contactDataWithoutCompany
      }));

      // In edit mode, if a company is selected, create company-contact relationship
      if (this.pendingCompanyId) {
        this.store.dispatch(CrmActions.createCompanyContact({
          company_id: this.pendingCompanyId,
          contact_id: contactData.id,
          is_default: 1
        }));

        // Clear the pending company ID
        this.pendingCompanyId = null;
      }
    } else {
      // Create new contact
      this.store.dispatch(CrmActions.createContact({ contact: contactDataWithoutCompany }));

      // Listen for successful contact creation to create company-contact relationship
      if (this.pendingCompanyId) {
        this.store.select(CrmSelectors.selectContacts)
          .pipe(
            filter(contacts => contacts && contacts.length > 0),
            takeUntil(this.destroy$)
          )
          .subscribe(contacts => {
            // Get the newly created contact (assuming it's the most recent one)
            const newContact = contacts[0]; // Assuming the most recent contact is first

            if (newContact && newContact.id && this.pendingCompanyId) {
              // Create company-contact relationship
              this.store.dispatch(CrmActions.createCompanyContact({
                company_id: this.pendingCompanyId,
                contact_id: newContact.id,
                is_default: 1
              }));

              // Clear the pending company ID
              this.pendingCompanyId = null;
            }
          });
      }
    }

    // Close modal immediately
    this.showAddContactModal = false;
  }

  syncWithHubspot(user: any): void {
    console.log('Sync with HubSpot:', user);
    // TODO: Implement HubSpot sync functionality
  }

  getExtraCompaniesText(extraCompanies: any[]): string {
    return extraCompanies.map(cc => cc.company?.name || 'Unknown Company').join(', ');
  }

  /**
   * Resolve a logo URL for a CRMCompany by matching it against the names of
   * companies enriched in the contact's source report. Returns null when the
   * contact didn't come from a report, or when no company name matches.
   *
   * Match is case-insensitive and whitespace-trimmed for resilience against
   * minor formatting drift between SearchCompany.name and CRMCompany.name.
   */
  getCompanyLogo(contact: any, companyName?: string | null): string | null {
    if (!companyName) {
      return null;
    }
    const reportCompanies: any[] = contact?.report?.companies;
    if (!Array.isArray(reportCompanies) || reportCompanies.length === 0) {
      return null;
    }
    const target = companyName.trim().toLowerCase();
    const hit = reportCompanies.find(rc =>
      typeof rc?.name === 'string' && rc.name.trim().toLowerCase() === target
    );
    return hit?.logo_url || null;
  }

  onDeleteCompanyContact(companyContactId: number): void {
    this.store.dispatch(CrmActions.deleteCompanyContact({ id: companyContactId }));

    // Refresh the current contact details to show updated company list
    if (this.selectedContact?.id) {
      this.store.dispatch(CrmActions.loadContactDetail({ id: this.selectedContact.id.toString() }));
    }
  }

  // Avatars whose image failed to load; we fall back to initials without mutating store state.
  private brokenAvatarIds = new Set<any>();

  onAvatarError(contact: any): void {
    if (contact?.id != null) {
      this.brokenAvatarIds.add(contact.id);
    }
  }

  isAvatarBroken(contact: any): boolean {
    return contact?.id != null && this.brokenAvatarIds.has(contact.id);
  }

  /** Open the linked report in a new tab. */
  viewReport(contact: any, event?: MouseEvent): void {
    event?.stopPropagation();
    const reportId = contact?.report?.id;
    if (!reportId) {
      return;
    }
    window.open(`/report/${reportId}`, '_blank', 'noopener,noreferrer');
  }

  /** Open the Add-Deal modal with this contact pre-selected. */
  openAddDealForContact(contact: any, event?: MouseEvent): void {
    event?.stopPropagation();
    this.preselectedContactIdForDeal = contact?.id ?? null;
    this.showAddDealModal = true;
  }

  onCloseAddDealModal(): void {
    this.showAddDealModal = false;
    this.preselectedContactIdForDeal = null;
  }

  onSaveDeal(dealData: any): void {
    const selectedContactId = dealData.contactIds?.[0] ?? null;
    const selectedCompanyId = dealData.companyIds?.[0] ?? null;

    if (!selectedContactId || !selectedCompanyId) {
      alert('Please select both a contact and a company for the deal.');
      return;
    }

    const deal = {
      name: dealData.dealName,
      deal_value: dealData.amount || 0,
      company_id: selectedCompanyId,
      contact_id: selectedContactId,
      icp_score: 0,
      lead_source: 'SalesOMMO',
      user_id: dealData.dealOwner || 1,
      deal_stage: dealData.dealStage || 'early',
      workspace_id: 1,
      close_date: dealData.closeDate,
      type: 'service'
    };

    this.store.dispatch(CrmActions.createDeal({ deal }));
    this.onCloseAddDealModal();
  }
}
